/**
 * Merging filter changes into the query string that drives /search and the topic and
 * collection pages.
 *
 * Every caller starts from the params already on screen, so a filter can only be
 * dropped by deleting it explicitly — omitting it from `changes` leaves whatever an
 * earlier click, or a shared link, put there.
 */

/**
 * The current params with `changes` applied. A value of `null` or `""` removes the
 * param rather than setting it empty.
 *
 * Pagination is always reset. Results paginate by appending ("Show more"), so carrying
 * a page number across a filter change made the new filter's page 2 append onto the old
 * filter's page 1, and the list then showed two different searches at once.
 *
 * Takes a copy rather than mutating `current`, which is live router state.
 */
export function withFilters(
  current: URLSearchParams,
  changes: Record<string, string | null>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete("page");
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  }
  return next;
}

/**
 * The change a Listen / Read / Watch pill makes. `null` means All.
 *
 * `type` is always cleared, and that is the whole point of this function existing.
 * `consumption` filters on the badge; `type` picks which table the search endpoint
 * queries at all -- Post or Media. The topic page used to send both, pinning
 * `type=Media` behind Listen and Watch. Everything the ingestion pipeline publishes is
 * a Post, so those two filters searched a table holding none of it and came back empty,
 * while Read (`type=Post`) went on looking fine. Consumption alone spans both tables.
 *
 * Clearing rather than omitting matters: filters are merged into the params already in
 * the URL, so a `type` left by an earlier click or a shared link would keep applying.
 */
export function consumptionFilter(consumption: string | null): Record<string, string | null> {
  return { consumption, type: null };
}
